import { useListDevices } from "#hooks/adapters/utils/useDevices";
import {
  useTableSortingAndPagination,
  useTablesSortingAndPagination,
} from "#redux/reducers/tableStateReducer";
import { useParams } from "#routers/hooks";
import { linkToDeviceDetail } from "#routers/links";
import { FacilityDetailPageParams } from "#routes/organization/facilities/[facilityId]";
import { CreateAssetDropdownMenu } from "#src/batteries-included-components/Buttons/CreateAssetDropdownMenu/CreateAssetDropdownMenu";
import { DevicesTableFilterArea } from "#src/batteries-included-components/FilterAreas/AssetsFilterAreas";
import { IsFeatureAvailable } from "#src/batteries-included-components/Layouts/Authorization/IsFeatureAvailable";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink/RoutingLink";
import { useCustomAttributeFiltersV2 } from "#src/components/hooks/FilterPanel/useCustomAttributeFilters";
import useLocalization from "#src/hooks/useLocalization";
import { useStickyState } from "#src/hooks/useStickyState";
import { useStorageKey } from "#src/hooks/useStorageKey";
import {
  useDataTableGroupBy,
  useDataTableGroupByProps,
  useDataTableMetaExtendedHeaders,
  useDataTableMetaHeaders,
} from "#src/utils/table";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  DataTable,
  DataTablePanel,
  Dialog,
  getDataTableGroupByKeysForGroupByData,
  HeaderType,
  Icon,
  Pill,
  StorageKeys,
  useToast,
} from "@validereinc/common-components";
import {
  AssetType,
  DeviceAdapter,
  DeviceAdapterQueryKeys,
  DeviceType,
  isResponseErrorType,
  Resources,
  SortDirection,
  type GroupedByEntryType,
  type ResponseErrorType,
} from "@validereinc/domain";
import {
  getYearMonthFromDateRange,
  HTTPForbiddenError,
  HTTPNotFoundError,
  HTTPResponseError,
  toTitleCaseString,
} from "@validereinc/utilities";
import pLimit from "p-limit";
import React, { useCallback, useMemo, useState } from "react";

export const DevicesPanel = ({
  viewConfigStorageKey,
  groupByTablesStateStorageKey,
}: Pick<
  StorageKeys,
  "viewConfigStorageKey" | "groupByTablesStateStorageKey"
>) => {
  const { facilityId } = useParams<FacilityDetailPageParams>();

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { localize, isLoading: isLoadingLocalization } = useLocalization();
  const [tableConfigStorageKey, filterConfigStorageKey] = useStorageKey(
    ["table-configuration", "filter-configuration"],
    "facilities-devices"
  );
  const [tableStateStorageKey] = useStorageKey(
    ["table-state"],
    ["facilities-devices", facilityId]
  );
  const { customAttributeHeaders, isLoading: isCustomAttributesLoading } =
    useCustomAttributeFiltersV2({
      assetType: AssetType.DEVICE,
      groupable: true,
    });
  const [viewFilters] = useStickyState<any>({}, viewConfigStorageKey);
  const [tableFilters] = useStickyState({}, filterConfigStorageKey);
  const filters = { ...tableFilters, "facility.id": facilityId };

  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "name",
      sortDirection: SortDirection.ASCENDING,
      pageSize: 25,
    },
    filters,
    tableStateStorageKey
  );

  const devicesQueryPayload: Parameters<typeof DeviceAdapter.getList>[0] = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    groupBy: tableState.groupBy,
    filters,
    meta: {
      period: getYearMonthFromDateRange(viewFilters.period) ?? "",
    },
  };
  const { data, isLoading, isFetching } = useListDevices(devicesQueryPayload);
  const items = (data as any)?.data ?? [];

  const groupByKeys = getDataTableGroupByKeysForGroupByData(
    items as unknown as GroupedByEntryType[]
  );
  const [groupByTablesStates, updateGroupByTablesStates] =
    useTablesSortingAndPagination(
      groupByKeys,
      {
        ...devicesQueryPayload,
        page: 1,
        pageSize: 10,
        sortBy: "created_at",
        sortDirection: SortDirection.DESCENDING,
      },
      groupByTablesStateStorageKey
    );

  const {
    queryPayloads: groupByQueryPayloads,
    setActiveGroupByGroups,
    totalSelectableItems,
  } = useDataTableGroupBy(
    items as unknown as GroupedByEntryType[],
    tableState.groupBy,
    groupByKeys.map(
      (key) => groupByTablesStates.get(key) ?? devicesQueryPayload
    )
  );

  const devicesGroupedByQueries = useQueries({
    queries: groupByQueryPayloads.map((d) => ({
      queryKey: DeviceAdapterQueryKeys.getList(d.payload),
      queryFn: () => DeviceAdapter.getList(d.payload),
      enabled: d.enabled,
    })),
  });

  const metaHeaders = useDataTableMetaHeaders();
  const metaExtendedHeaders = useDataTableMetaExtendedHeaders(
    tableState.groupBy.length
      ? devicesGroupedByQueries.flatMap((q) => q.data?.data ?? [])
      : items
  );

  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [selectedRows, setSelectedRows] = useState<Record<string, DeviceType>>(
    {}
  );

  const deleteMutation = useMutation({
    mutationFn: (params: Parameters<typeof DeviceAdapter.deleteOne>[0]) => {
      return DeviceAdapter.deleteOne(params);
    },
    onSuccess: (_, params) => {
      toast.push({
        intent: "success",
        description: `Successfully deleted ${localize(Resources.DEVICE)}${params.previousData?.name ? ` ${params.previousData?.name}.` : "."}`,
      });
      queryClient.invalidateQueries({ queryKey: [Resources.DEVICE] });
    },
    onError: (error: any) => {
      if (
        error instanceof HTTPResponseError &&
        error.responseBodyJson &&
        isResponseErrorType(error.responseBodyJson)
      ) {
        toast.push({
          intent: "error",
          description: `Unable to delete ${localize(Resources.DEVICE)} due to a server error: ${error.responseBodyJson.error}`,
        });
      } else {
        toast.push({
          intent: "error",
          description: `Unable to delete ${localize(Resources.DEVICE)}. An unknown error occurred.`,
        });
      }
    },
  });

  const coreHeaders: Array<HeaderType<DeviceType>> = [
    {
      key: "name",
      label: "Name",
      isSortable: true,
      renderComponent: ({ item }) => (
        <RoutingLink to={linkToDeviceDetail(item.id)}>{item.name}</RoutingLink>
      ),
    },
    {
      key: "type.name",
      label: "Type",
      // REVIEW: cannot be enabled until item access matches key, and key 'type.name' to sort or group on is not valid
      // isGroupable: true,
    },
    {
      label: "Status",
      key: "status",
      isSortable: true,
      isGroupable: true,
      renderComponent: ({ item }) => (
        <Pill variant={item.status === "active" ? "success" : "default"}>
          {toTitleCaseString(item.status, {
            intent: "ui",
            exceptions: ["oil-and-gas"],
          })}
        </Pill>
      ),
    },
    {
      label: "Created Date",
      key: "created_at",
      isSortable: true,
      renderComponent: ({ item }) => (
        <DataTable.DataRow.DateCell
          value={item.created_at}
          withTime
        />
      ),
    },
  ];
  const headers: Array<HeaderType<DeviceType>> = useMemo(
    () => [
      ...coreHeaders,
      ...customAttributeHeaders,
      ...metaHeaders,
      ...metaExtendedHeaders,
    ],
    [coreHeaders, customAttributeHeaders, metaHeaders, metaExtendedHeaders]
  );

  const actionRow = (
    <CreateAssetDropdownMenu
      shouldUseLightVariant
      assetType={AssetType.DEVICE}
    />
  );

  const getItemBulkActions = useCallback(
    ({ buttonProps }) => [
      <IsFeatureAvailable
        key="delete"
        featureFlagQuery="core:devices"
        permissionQuery="devices:delete"
        fallbackChildren={null}
      >
        <Button
          {...buttonProps}
          variant="error-outline"
          slotLeft={<Icon variant="trash" />}
          onClick={() => setIsDeleteConfirmationOpen(true)}
        >
          Delete
        </Button>
      </IsFeatureAvailable>,
    ],
    []
  );

  const groupByDataTableProps = useDataTableGroupByProps({
    items,
    groupBy: tableState.groupBy,
    updateTableState,
    setActiveGroupByGroups,
    groupByQueries: devicesGroupedByQueries,
    groupByTablesStates,
    updateGroupByTablesStates,
    headers,
    getItemId: (item) => item.id,
    getItemBulkActions,
    totalSelectableItems,
    selected: selectedRows,
    onSelectionChange: setSelectedRows,
  });

  return (
    <>
      <DataTablePanel
        storageKey={tableConfigStorageKey}
        panelProps={{
          title: "Devices",
          actionRow,
        }}
        filterComponent={
          <DevicesTableFilterArea
            filterConfigStorageKey={filterConfigStorageKey}
          />
        }
        dataTableProps={{
          intent: "simplicity-first",
          items,
          headers,
          isLoading:
            isLoading || isLoadingLocalization || isCustomAttributesLoading,
          isBusy: isFetching || deleteMutation.isLoading,
          getItemId: (item) => item.id,
          getItemBulkActions,
          selected: selectedRows,
          onSelectionChange: setSelectedRows,
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          pagination: {
            page: tableState.page,
            pageSize: tableState.pageSize,
            total: (data as any)?.total_entries,
          },
          onSortChange: updateTableState,
          onPaginationChange: updateTableState,
        }}
        {...groupByDataTableProps}
      />
      <IsFeatureAvailable
        featureFlagQuery="core:devices"
        permissionQuery="devices:delete"
        fallbackChildren={null}
      >
        <Dialog
          title={`Delete ${localize(`${Resources.DEVICE}_plural`)}?`}
          isOpen={isDeleteConfirmationOpen}
          onClose={() => setIsDeleteConfirmationOpen(false)}
          actionRow={[
            <Button
              key="submit"
              variant="error"
              isLoading={deleteMutation.isLoading}
              onClick={() => {
                const selectedRowIds = Object.keys(selectedRows);
                const limitPromises = pLimit(8);

                setIsDeleteConfirmationOpen(false);
                toast
                  .pushAsync(
                    Promise.all(
                      selectedRowIds.map((id) =>
                        limitPromises(async () =>
                          deleteMutation.mutateAsync({
                            id,
                            previousData: selectedRows[id],
                          })
                        )
                      )
                    ),
                    {
                      intent: "note",
                      description: `Deleting ${selectedRowIds.length} selected ${localize(`${Resources.DEVICE}_plural`)}...`,
                      isBusy: deleteMutation.isLoading,
                    },
                    {
                      isDismissible: false,
                      duration: Infinity,
                    }
                  )
                  .then(({ data, toast: firstToast }) => {
                    toast.push(
                      {
                        intent: "success",
                        description: `Successfully deleted ${data.length} ${localize(`${Resources.DEVICE}_plural`)}`,
                      },
                      {
                        id: firstToast.id,
                        isDismissible: true,
                        duration: 5000,
                      }
                    );
                    setSelectedRows({});
                  })
                  .catch(({ error, toastId }) => {
                    const isNotFoundError = error instanceof HTTPNotFoundError;
                    const isForbiddenError =
                      error instanceof HTTPForbiddenError;
                    const isNodeAPIError =
                      error instanceof HTTPResponseError &&
                      isResponseErrorType(error.responseBodyJson);

                    toast.push(
                      {
                        intent: "error",
                        description: isNodeAPIError
                          ? `Failed to delete selected ${localize(`${Resources.DEVICE}_plural`)}: ${(error.responseBodyJson as unknown as ResponseErrorType | undefined)?.error}`
                          : isNotFoundError || isForbiddenError
                            ? `Failed to delete selected ${localize(`${Resources.DEVICE}_plural`)}: ${error.getExplanation()}`
                            : `Failed to delete ${localize(`${Resources.DEVICE}_plural`)}.`,
                      },
                      {
                        id: toastId,
                        isDismissible: true,
                        duration: 5000,
                      }
                    );
                  })
                  .finally(() => {
                    queryClient.invalidateQueries({
                      queryKey: [Resources.DEVICE],
                    });
                  });
              }}
            >
              Delete
            </Button>,
          ]}
        >
          You&apos;re deleting {Object.keys(selectedRows).length}&nbsp;
          {localize(`${Resources.DEVICE}_plural`)}. This action cannot be
          undone. Are you sure you want to continue?
        </Dialog>
      </IsFeatureAvailable>
    </>
  );
};
